import * as THREE from 'three';
import { IUpdateModelParams } from '@/editor/data/model/model';
import BaseModel2d from './BaseModel2d';
import { globalStores } from '@/editor/stores';
import { pixelRatio2d } from '@/editor/math/const';
import { getUniqueId } from '@/editor/utils';
import { EEntityType } from '@/editor/types';
import { ENTITY_LATER, ENTITY_PRIORITY, ENTITY_RENDER_ORDER, workPointColor } from '@/editor/constant/entity';
import { EContainerType } from '@/editor/types/entity';
import { disposeAllChildren, disposeAllChildrenThenRemoveFromParent } from '../utils';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { modelManager } from '@/editor/manager/entityManager';

const WORK_POINT_RADIUS_MM = 300;

const workPointFillMaterial = new THREE.MeshBasicMaterial({
  color: workPointColor,
  opacity: 0.5,
  transparent: true,
  depthTest: false,
  depthWrite: false,
  side: THREE.DoubleSide
});

const placeholderFillMaterial = new THREE.MeshBasicMaterial({
  color: 0xffffff,
  opacity: 0.05,
  transparent: true,
  depthTest: false,
  depthWrite: false,
  side: THREE.DoubleSide
});

// 透明 hitbox 材质，用于 raycast
const hitboxMaterial = new THREE.MeshBasicMaterial({
  color: 0x000000,
  opacity: 0,
  transparent: true,
  depthTest: false,
  depthWrite: false,
  side: THREE.DoubleSide
});

// 缓存的 hitbox 几何体（按尺寸缓存）
const hitboxGeometryCache = new Map<string, THREE.PlaneGeometry>();

function getHitboxGeometry(width: number, height: number): THREE.PlaneGeometry {
  const key = `${width.toFixed(2)}_${height.toFixed(2)}`;
  let geom = hitboxGeometryCache.get(key);
  if (!geom) {
    geom = new THREE.PlaneGeometry(width, height);
    hitboxGeometryCache.set(key, geom);
  }
  return geom;
}

/**
 * 2D 家具模型
 * 只负责渲染 hitbox（用于 raycast）和 workPoints
 * 实际渲染由 FurnitureInstanceManager 管理的 InstancedMesh 完成
 */
class FurnitureModel extends BaseModel2d {
  private _entityId: string;
  private _width: number = 0;
  private _thickness: number = 0;
  private _originWidth: number = 0;
  private _originHeight: number = 0;
  private _workPoints: IPoint2d[] = [];
  private _workPointsVisible = false;
  private _workPointGroup: THREE.Group | null = null;
  private _placeholderGroup: THREE.Group | null = null;
  private _hitboxMesh: THREE.Mesh | null = null;
  private _hitboxGroup: THREE.Group | null = null;
  private _hasInstance = false; // 是否已注册到 InstancedMesh

  constructor(params?: { id: string }) {
    super();

    const id = params?.id || getUniqueId();
    this._entityId = id;
    this._modelGroup.userData.type = EEntityType.Furniture;
    this._modelGroup.userData.priority = ENTITY_PRIORITY.furniture;
    this._modelGroup.userData.entityId = id;
    this._modelGroup.userData.containerType = EContainerType.entity;

    this.setDisplayOrder(ENTITY_RENDER_ORDER.furniture + 0.1);

    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);
  }

  get group() {
    return this._modelGroup;
  }

  get entityId() {
    return this._entityId;
  }

  /**
   * 完整更新（会重建 hitbox）
   */
  update(params: Omit<IUpdateModelParams, 'id' | 'height' | 'offGround' | 'model3d' | 'flip'> & { modelId: string }) {
    const { center, model2d, modelId, width, thickness, rotation, workPoints, workPointsVisible } = params;

    this._width = width;
    this._thickness = thickness;
    this._workPoints = workPoints ?? this._workPoints;
    if (typeof workPointsVisible === 'boolean') {
      this._workPointsVisible = workPointsVisible;
    }

    this.clear();
    this._workPointGroup = null;
    this._placeholderGroup = null;

    // 注册到 InstancedMesh（如果有 modelId 和 model2d）
    if (modelId && model2d && width && thickness) {
      const instanceManager = modelManager.furnitureManager.instanceManager;
      this._hasInstance = instanceManager.registerInstance(this._entityId, modelId, model2d);

      if (this._hasInstance) {
        // 获取 asset 信息
        const assetInfo = instanceManager.getAssetInfo(this._entityId);
        if (assetInfo) {
          this._originWidth = assetInfo.originWidth;
          this._originHeight = assetInfo.originHeight;

          // 创建 hitbox
          this.createHitbox(assetInfo.originWidth, assetInfo.originHeight, assetInfo.zOffset);

          // 更新 InstancedMesh 变换
          instanceManager.updateTransform(this._entityId, {
            position: center,
            rotation,
            width,
            thickness
          });
        }
      }
    }

    // 更新位置和旋转（用于 workPoints）
    this._modelGroup.position.setX(center.x / pixelRatio2d);
    this._modelGroup.position.setY(center.y / pixelRatio2d);
    this._modelGroup.rotation.z = rotation;

    this._updateScale();
    this._updateWorkPointsGroup();
    this._updatePlaceholder();
  }

  /**
   * 轻量更新（不重建 hitbox）
   */
  updateWithoutDestroy(
    params: Pick<IUpdateModelParams, 'center' | 'rotation' | 'status' | 'width' | 'thickness'> & {
      workPoints?: IPoint2d[];
      workPointsVisible?: boolean;
    }
  ) {
    const { center, rotation, width, thickness, workPoints, workPointsVisible } = params;
    this._width = width;
    this._thickness = thickness;

    if (workPoints) {
      this._workPoints = workPoints;
    }

    if (typeof workPointsVisible === 'boolean') {
      this._workPointsVisible = workPointsVisible;
    }

    this._modelGroup.position.setX(center.x / pixelRatio2d);
    this._modelGroup.position.setY(center.y / pixelRatio2d);
    this._modelGroup.rotation.z = rotation;

    this._updateScale();

    // 更新 InstancedMesh 变换
    if (this._hasInstance) {
      const instanceManager = modelManager.furnitureManager.instanceManager;
      instanceManager.updateTransform(this._entityId, {
        position: center,
        rotation,
        width,
        thickness
      });

      // 更新 hitbox z 偏移
      this._updateHitboxZ();
    }

    this._updateWorkPointsGroup();
    this._updatePlaceholder();
  }

  hide() {
    const instanceManager = modelManager.furnitureManager.instanceManager;
    instanceManager.hideInstance(this._entityId);
  }

  private createHitbox(originWidth: number, originHeight: number, zOffset: number) {
    if (this._hitboxGroup) {
      disposeAllChildrenThenRemoveFromParent(this._hitboxGroup);
    }

    this._hitboxGroup = new THREE.Group();
    this._hitboxGroup.userData.originWidth = originWidth;
    this._hitboxGroup.userData.originHeight = originHeight;
    this._modelGroup.add(this._hitboxGroup);

    const hitboxGeometry = getHitboxGeometry(originWidth, originHeight);
    this._hitboxMesh = new THREE.Mesh(hitboxGeometry, hitboxMaterial);
    this._hitboxMesh.renderOrder = ENTITY_RENDER_ORDER.furniture;
    this._hitboxMesh.position.z = zOffset;
    this._hitboxGroup.add(this._hitboxMesh);
  }

  private _updateScale() {
    if (!this._originWidth || !this._originHeight) return;

    this._modelGroup.scale.set(
      this._width / this._originWidth / pixelRatio2d,
      this._thickness / this._originHeight / pixelRatio2d,
      1
    );
  }

  private _updateHitboxZ() {
    if (!this._hitboxMesh) return;
    const instanceManager = modelManager.furnitureManager.instanceManager;
    const zOffset = instanceManager.getZOffset(this._entityId);
    this._hitboxMesh.position.z = zOffset;
  }

  private ensureWorkPointGroup() {
    if (!this._workPointGroup) {
      this._workPointGroup = new THREE.Group();
      this._workPointGroup.renderOrder = ENTITY_RENDER_ORDER.furnitureWorkPoint;
      this._workPointGroup.position.set(0, 0, 0);
      this._modelGroup.add(this._workPointGroup);
    } else if (this._workPointGroup.parent !== this._modelGroup) {
      this._modelGroup.add(this._workPointGroup);
    }

    return this._workPointGroup;
  }

  private _updateWorkPointsGroup() {
    if (!this._workPointsVisible || this._workPoints.length === 0 || !this._originWidth || !this._originHeight) {
      if (this._workPointGroup) {
        disposeAllChildren(this._workPointGroup);
        this._workPointGroup.visible = false;
      }
      return;
    }

    if (!this._width || !this._thickness) {
      if (this._workPointGroup) {
        disposeAllChildren(this._workPointGroup);
        this._workPointGroup.visible = false;
      }
      return;
    }

    const group = this.ensureWorkPointGroup();
    disposeAllChildren(group);

    const scaleX = Math.abs(this._modelGroup.scale.x) > Number.EPSILON ? Math.abs(this._modelGroup.scale.x) : 1;
    const scaleY = Math.abs(this._modelGroup.scale.y) > Number.EPSILON ? Math.abs(this._modelGroup.scale.y) : 1;

    for (const point of this._workPoints) {
      const x = point.x;
      const y = point.y;

      // Map work-point offsets (in model units) into the SVG's local space before scaling.
      const xLocal = (x / this._width) * this._originWidth;
      const yLocal = (-y / this._thickness) * this._originHeight;

      const marker = this.createWorkPointMarker(scaleX, scaleY);
      marker.position.set(xLocal, -yLocal, 0);
      group.add(marker);
    }

    group.visible = this._workPointsVisible;
  }

  private createWorkPointMarker(scaleX: number, scaleY: number) {
    const radiusWorld = WORK_POINT_RADIUS_MM / pixelRatio2d;
    const safeScaleX = Math.abs(scaleX) > Number.EPSILON ? Math.abs(scaleX) : 1;
    const safeScaleY = Math.abs(scaleY) > Number.EPSILON ? Math.abs(scaleY) : 1;
    const scaleXFactor = radiusWorld / safeScaleX;
    const scaleYFactor = radiusWorld / safeScaleY;

    const container = new THREE.Group();
    container.renderOrder = ENTITY_RENDER_ORDER.furnitureWorkPoint;

    const fillGeometry = new THREE.CircleGeometry(1, 32);
    const fillMesh = new THREE.Mesh(fillGeometry, workPointFillMaterial);
    fillMesh.renderOrder = ENTITY_RENDER_ORDER.furnitureWorkPoint;
    fillMesh.scale.set(scaleXFactor, scaleYFactor, 1);
    fillMesh.layers.set(ENTITY_LATER.WORK_POINT_LAYER);
    container.add(fillMesh);

    return container;
  }

  private ensurePlaceholderGroup() {
    if (!this._placeholderGroup) {
      this._placeholderGroup = new THREE.Group();
      this._placeholderGroup.renderOrder = ENTITY_RENDER_ORDER.furniture;
      this._placeholderGroup.position.set(0, 0, 0);
      this._modelGroup.add(this._placeholderGroup);
    } else if (this._placeholderGroup.parent !== this._modelGroup) {
      this._modelGroup.add(this._placeholderGroup);
    }

    this._placeholderGroup.visible = true;
    return this._placeholderGroup;
  }

  private _updatePlaceholder() {
    // 如果已有 instance，不需要 placeholder
    if (this._hasInstance) {
      this._disposePlaceholder();
      return;
    }

    if (!this._width || !this._thickness) {
      this._disposePlaceholder();
      return;
    }

    const group = this.ensurePlaceholderGroup();
    disposeAllChildren(group);

    const widthWorld = this._width / pixelRatio2d;
    const thicknessWorld = this._thickness / pixelRatio2d;

    const fillGeometry = new THREE.PlaneGeometry(widthWorld, thicknessWorld);
    const fillMesh = new THREE.Mesh(fillGeometry, placeholderFillMaterial);
    fillMesh.renderOrder = ENTITY_RENDER_ORDER.furniture;
    group.add(fillMesh);
  }

  private _disposePlaceholder() {
    if (!this._placeholderGroup) return;
    disposeAllChildrenThenRemoveFromParent(this._placeholderGroup);
    this._placeholderGroup = null;
  }

  override dispose() {
    // 从 InstancedMesh 注销
    if (this._hasInstance) {
      modelManager.furnitureManager.instanceManager.unregisterInstance(this._entityId);
      this._hasInstance = false;
    }

    this._hitboxMesh = null;

    if (this._hitboxGroup) {
      disposeAllChildrenThenRemoveFromParent(this._hitboxGroup);
      this._hitboxGroup = null;
    }

    if (this._workPointGroup) {
      disposeAllChildrenThenRemoveFromParent(this._workPointGroup);
      this._workPointGroup = null;
    }

    if (this._placeholderGroup) {
      disposeAllChildrenThenRemoveFromParent(this._placeholderGroup);
      this._placeholderGroup = null;
    }

    super.dispose();
  }
}

export default FurnitureModel;
